import { DOCUMENT, ViewportScroller } from '@angular/common';
import { Component, HostListener, Inject, OnDestroy, OnInit, ViewEncapsulation, afterNextRender } from '@angular/core';
import { ChatService } from 'src/app/core/services/chat.service';
import { CommonService } from 'src/app/core/services/common.service';
import { HeaderData, HeaderFooterService } from 'src/app/core/services/header-footer.service';
import { SearchModal } from 'src/app/core/services/search-modal.service';
import { QueryStringService } from 'src/app/core/services/query-string.service';
import { Subscription, debounceTime, fromEvent } from 'rxjs';
import { CoreModule } from 'src/app/core/core.module';
import { NavMenuComponent } from '../nav-menu/nav-menu.component';
import { SearchModalComponent } from '../search-modal/search-modal.component';
import { PartnerDataService } from 'khegm-ui-components';
import { NinetailedService } from 'src/app/core/services/ninetailed.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.less'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [CoreModule, NavMenuComponent, SearchModalComponent],
})
export class HeaderComponent implements OnInit, OnDestroy {

  headerData: HeaderData = {} as HeaderData;
  DefaultHeaderData: HeaderData = {} as HeaderData;
  isPhonePzd = false; // skeleton flag scoped to the phone number while personalization resolves
  private p13nTimeouts: ReturnType<typeof setTimeout>[] = [];
  private detachHeaderVariantListener: (() => void) | null = null;
  private headerP13nSub: Subscription | null = null;
  isMobile = false;
  isChatInitialized = false;
  isSearchOpen = false;
  isChatLoaded = false;
  active = false;
  isContactUsOpen = false;
  logoUrl = '';
  studentLoginUrl = '';
  applyNowUrl = '';
  scrollY = 0;
  scrollPosition: [number, number] = [0, 0];
  partnerInfo: any;
  showPartnerLogo = false;

  @HostListener('click', ['$event.target']) onDocumentClick(target: EventTarget | null) {
    const anchor = (target as HTMLElement)?.closest?.('a');
    if (!anchor) return;

    this.searchService.close();

    if (this.active) {
      this.active = false;
      this.dom.body.classList.remove('no-scroll');
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }

  @HostListener('window:scroll', ['$event']) onWindowScroll(event: Event) {
    this.scrollY = window.scrollY;
  }

  constructor(
    private headerService: HeaderFooterService,
    private commonService: CommonService,
    private chatService: ChatService,
    private searchService: SearchModal,
    private viewPortScroller: ViewportScroller,
    private queryStringService: QueryStringService,
    private partner: PartnerDataService,
    private ninetailedService: NinetailedService,
    @Inject(DOCUMENT) protected dom: Document,
  ) {
    
    afterNextRender(() => {
      this.setScrollOffset();
      fromEvent(window, 'resize')
        .pipe(debounceTime(200))
        .subscribe(() => this.setScrollOffset());
    })

  }

  setScrollOffset() {
    if (window.innerWidth <= 639) {
      this.viewPortScroller.setOffset([0, 150])
    } else if (640 <= window.innerWidth && window.innerWidth <= 1199) {
      this.viewPortScroller.setOffset([0, 100])
    } else if (window.innerWidth >= 1200) {
      this.viewPortScroller.setOffset([0, 150])
    }
  }

  ngOnInit(): void {
    // Listen for header configuration changes.
    this.headerService.headerConfiguration.subscribe((headerData: HeaderData | false | undefined) => {
      if (headerData === undefined) return;
      // if value 'false' is passed then use the defaultHeaderData from the service. This used for the override purpose.
      this.headerData = headerData || this.headerService.defaultHeaderData;
      // Initialize chat widget
      this.initChat();

      if(this.commonService.isPlatformBrowser()) {
        this.setScrollOffset();
        this.logoUrl = this.setLogoLink();
        this.studentLoginUrl = this.setStudentLoginLink();
        this.applyNowUrl = this.setApplyNowLink();
        this.showPartnerLogo = true;
        this.applyHeaderPersonalization(this.headerData);
      }

    });

    this.partner.getPartnerData().subscribe(data => {
      if (data) {
        this.partnerInfo = data;
      }
    });

    this.searchService.watch().subscribe((state: boolean) => {
      this.isSearchOpen = state;
    });

    this.chatService.chatWidgetLoaded$.subscribe(data => {
      if(data!==null){
        this.isChatLoaded = data;
      }
    })

    if(this.commonService.isPlatformBrowser()) {
      // Listen for mobile resolution activation
      this.commonService.desktopUp$.subscribe(isDeskTop => {
        this.isMobile = !isDeskTop;
      })
    }

  }

  toggleMenu() {
    this.active = !this.active;
    this.dom.body.classList.toggle('no-scroll', this.active);
    if (this.active) {
      this.scrollPosition = this.scrollY ? [0, this.scrollY] : this.viewPortScroller.getScrollPosition();
    } else {
      window.scrollTo({ top: this.scrollPosition[1], left: this.scrollPosition[0], behavior: 'instant' });
    }
    this.searchService.close();

  }

  openSearchBox() {
    this.active = false;
    this.dom.body.classList.remove('no-scroll');
    if (this.isSearchOpen) {
      this.searchService.close();
    } else {
      this.searchService.open();
    }
  }

  triggerChat() {
    this.chatService.chatButtonClick.next(true);
  }

  initChat() {
    if (this.commonService.isPlatformBrowser() && (!this.isChatInitialized || !this.isChatLoaded)) {
      this.chatService.addGenesysChatWidget();
      this.isChatInitialized = true;
    }
  }

  setLogoLink() {
    if (this.headerData.logoLink && this.headerData.logoLink.length > 0) {
      const logoLink = this.headerData.logoLink;
      const qsp = this.queryStringService.getFullQspString();
      return qsp ? `${logoLink}?${qsp}` : logoLink;
    } else {
      return '';
    }
  }

  setStudentLoginLink() {
    if (this.headerData.stdLoginTxt && this.headerData.stdLoginLink.length > 0) {
      const studentLoginLink = this.headerData.stdLoginLink.split('?').at(0);
      const existingQsp = this.headerData.stdLoginLink.includes('?') ? this.headerData.stdLoginLink.split('?').at(1) : '';
      let qsp = this.queryStringService.getFullQspString();
      qsp = existingQsp ? `${existingQsp}&${qsp}` : qsp;
      return qsp ? `${studentLoginLink}?${qsp}` : studentLoginLink;
    } else {
      return '';
    }
  }

  setApplyNowLink(): string {
    return this.queryStringService.setApplyNowLink(this.headerData?.buttonLink2?.internalLink?.url);
  }

  private applyHeaderPersonalization(headerData: HeaderData): void {
    if (headerData?.sys?.id) {
      this.ninetailedService.registerPageEntryIds([headerData.sys.id]);
    }
    if (!headerData?.sys?.id || !headerData.isPzd) {
      return;
    }
    const headerId = headerData.sys.id;

    // Clean up any previous run
    this.detachHeaderVariantListener?.();
    this.detachHeaderVariantListener = null;
    this.headerP13nSub?.unsubscribe();
    this.headerP13nSub = null;
    this.p13nTimeouts.forEach(t => clearTimeout(t));
    this.p13nTimeouts = [];

    // Show skeleton on the phone number until the variant resolves.
    this.isPhonePzd = true;
    // Safety net: clear skeleton after 8s regardless.
    this.p13nTimeouts.push(setTimeout(() => { this.isPhonePzd = false; }, 8000));

    const experienceVariables = this.ninetailedService.isPreviewSession() ? { preview: true } : {};
    this.headerP13nSub = this.ninetailedService.fetchExperiences(experienceVariables).subscribe({
      next: experiences => {
        const mappedExperiences = this.ninetailedService.mapExperiencesFromGql(experiences);
        this.ninetailedService.whenReady().then(() => {
          const relevantExperiences = mappedExperiences.filter(exp =>
            exp.components.some(c => c.baseline.id === headerId)
          );
          if (relevantExperiences.length === 0) {
            this.isPhonePzd = false;
            return;
          }

          // Capture baseline phone so it can be restored when preview switches back to baseline
          const baselinePhone = headerData.contactUsPhoneNumber;
          const baselinePhoneLink = headerData.phoneNumberLink;

          let resolved = false;
          const lockAfterFirstResolution = !this.ninetailedService.isPreviewSession();
          this.detachHeaderVariantListener = this.ninetailedService.observeVariant(headerData, relevantExperiences, (state) => {
            if (state.loading || (lockAfterFirstResolution && resolved)) {
              return;
            }
            resolved = true;
            const variantId = state.variant?.sys?.id || state.variant?.id;
            const isVariantDifferent = !!variantId && variantId !== headerData.sys?.id;
            if (state.variant && (isVariantDifferent || state.isPersonalized)) {
              const variant = state.variant as HeaderData;
              this.headerData = {
                ...this.headerData,
                contactUsPhoneNumber: variant.contactUsPhoneNumber,
                phoneNumberLink: variant.phoneNumberLink,
              };
              this.registerHeaderInsights(state);
            } else {
              this.headerData = {
                ...this.headerData,
                contactUsPhoneNumber: baselinePhone,
                phoneNumberLink: baselinePhoneLink,
              };
            }
            this.isPhonePzd = false;
          });
        });
      },
      error: () => {
        this.isPhonePzd = false;
      }
    });
  }

  private registerHeaderInsights(state: any): void {
    this.p13nTimeouts.push(setTimeout(() => {
      const element = this.dom.querySelector('[data-p13n-header]');
      this.ninetailedService.registerElementInsights(element, state);
    }));
  }

  ngOnDestroy(): void {
    this.detachHeaderVariantListener?.();
    this.detachHeaderVariantListener = null;
    this.headerP13nSub?.unsubscribe();
    this.headerP13nSub = null;
    this.p13nTimeouts.forEach(t => clearTimeout(t));
    this.p13nTimeouts = [];
  }

}
